'use client';

import React, { useEffect, useRef, useState } from 'react';

interface StatItem {
  value: string;
  numericValue: number;
  suffix: string;
  label: string;
  sub: string;
}

const stats: StatItem[] = [
  {
    value: '35+',
    numericValue: 35,
    suffix: '+',
    label: 'Cadenas Indexadas',
    sub: 'Tipos de negocios cubiertos',
  },
  {
    value: '500+',
    numericValue: 500,
    suffix: '+',
    label: 'Partners Registrados',
    sub: 'Negocios listos para el lanzamiento',
  },
  {
    value: '8',
    numericValue: 8,
    suffix: '',
    label: 'Categorías',
    sub: 'Desde hogar hasta tecnología',
  },
];

const testimonials = [
  {
    quote: '¡Por fin una app que entiende cómo funciona Venezuela! Registré mi negocio en minutos y ya tengo clientes esperando el lanzamiento.',
    name: 'Carlos Bermúdez',
    role: 'Valencia',
    initials: 'CB',
    color: '#F48D31',
  },
  {
    quote: 'Siempre me pasaba ir a una tienda y no encontrar lo que buscaba. AQUÍ APP va a resolver eso de una vez por todas.',
    name: 'Mariangel Torres',
    role: 'Barquisimeto',
    initials: 'MT',
    color: '#00C9B8',
  },
  {
    quote: 'Como dueña de una boutique, necesitaba visibilidad local sin pagar fortunas en publicidad. AQUÍ APP es exactamente lo que necesitaba.',
    name: 'Patricia Rojas',
    role: 'Maracaibo',
    initials: 'PR',
    color: '#02D7D7',
  },
];

function CounterStat({ stat, isVisible }: { stat: StatItem; isVisible: boolean }) {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    hasAnimated.current = true;
    const duration = 2000;
    const steps = 60;
    const stepValue = stat.numericValue / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += stepValue;
      if (current >= stat.numericValue) {
        setCount(stat.numericValue);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, stat.numericValue]);

  return (
    <div className="stat-border-left pl-8 space-y-2">
      <p className="text-[10px] font-black uppercase tracking-[0.4em] text-muted">{stat.label}</p>
      <p className="text-6xl md:text-8xl font-black tracking-tighter text-white leading-none">
        {isVisible ? count : 0}{stat.suffix}
      </p>
      <p className="text-sm text-muted">{stat.sub}</p>
    </div>
  );
}

export default function StatsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            const hiddenEls = entry.target.querySelectorAll('.reveal-hidden');
            hiddenEls.forEach((el, i) => {
              setTimeout(() => el.classList.add('reveal-visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-32 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, transparent 0%, #001070 10%, #001070 90%, transparent 100%)' }}
    >
      {/* Decorative orb */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-[150px] opacity-10"
        style={{ background: '#F48D31' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Left: Stats */}
          <div className="space-y-12">
            <div className="space-y-4 reveal-hidden">
              <p className="text-[10px] font-black uppercase tracking-[0.5em] text-primary">Nuestros números</p>
              <h2 className="text-5xl md:text-7xl font-black tracking-tighter leading-none">
                Únete a la
                <br />
                <span className="gradient-text">comunidad</span>
                <br />
                AQUÍ APP
              </h2>
            </div>
            <div className="space-y-10">
              {stats.map((stat, i) => (
                <div key={i} className="reveal-hidden">
                  <CounterStat stat={stat} isVisible={isVisible} />
                </div>
              ))}
            </div>
            <div className="reveal-hidden">
              <a
                href="#registro"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-black text-sm uppercase tracking-widest text-white hover:scale-105 transition-all"
                style={{ background: '#F48D31', boxShadow: '0 8px 30px rgba(244,141,49,0.4)' }}
              >
                ¡Regístrate ahora!
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right: Testimonials */}
          <div className="space-y-5">
            <div className="reveal-hidden">
              <p className="text-[10px] font-black uppercase tracking-[0.5em] text-muted mb-6">Lo que dicen</p>
            </div>
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="reveal-hidden rounded-[24px] p-7 border group hover:border-white/15 transition-all duration-300"
                style={{
                  background: '#001A8A',
                  borderColor: 'rgba(255,255,255,0.06)',
                  transitionDelay: `${i * 100}ms`,
                }}
              >
                {/* Quote mark */}
                <div className="mb-4" style={{ color: t.color, opacity: 0.6 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                <p className="text-white/80 leading-relaxed text-[15px] mb-6 font-medium italic">
                  {t.quote}
                </p>
                <div className="flex items-center gap-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black text-bg"
                    style={{ background: t.color }}
                  >
                    {t.initials}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">{t.name}</p>
                    <p className="text-xs text-muted">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}